import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, InputAdornment, TextField } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import phoneNumberServices from 'app/services/phone-number-services';
import { isValidPhone, normalizePhoneInput } from 'app/utils/phone';

const DEFAULT_HELPER = 'Si no es de Chile, con su código de país (+54…).';

// Estados en que el formulario no puede guardar. 'unverified': Twilio no respondió, y sin su validación el teléfono no
// se puede cambiar (el backend tampoco lo acepta).
export const PHONE_BLOCKING_STATUSES = ['checking', 'invalid', 'unverified'];

// Teléfono que se valida con Twilio al salir del campo. Si es válido queda en formato internacional (+56912345678), que
// es lo que necesita WhatsApp; si no, dice por qué. El backend vuelve a validar al guardar.
// onStatusChange recibe 'idle' | 'checking' | 'valid' | 'unverified' | 'invalid', para bloquear el guardado.
const PhoneField = ({ value, onChange, onStatusChange, helperText = DEFAULT_HELPER, InputProps, ...props }) => {
    const [check, setCheck] = useState({ status: 'idle' });
    const checkedValue = useRef(null);
    const phone = value || '';

    useEffect(() => {
        onStatusChange?.(check.status);
    }, [check.status]); // eslint-disable-line react-hooks/exhaustive-deps

    // Lo que se escribe después de validar invalida el resultado; el número que dejó la validación no.
    useEffect(() => {
        if (phone !== checkedValue.current) setCheck({ status: 'idle' });
    }, [phone]);

    const validate = async () => {
        if (!phone || phone === checkedValue.current || !isValidPhone(phone)) return;

        setCheck({ status: 'checking' });
        try {
            const result = await phoneNumberServices.lookup(phone);
            if (result.status === 'unverified') {
                // Sin recordar el número: al volver a salir del campo se reintenta.
                checkedValue.current = null;
                setCheck({ status: 'unverified' });
                return;
            }
            const saved = result.phone_number || phone;
            checkedValue.current = saved;
            setCheck(result.valid ? { status: result.status, national: result.national_format } : { status: 'invalid', error: result.error });
            if (saved !== phone) onChange(saved);
        } catch (error) {
            checkedValue.current = null;
            setCheck({ status: 'idle' });
        }
    };

    const badFormat = !!phone && !isValidPhone(phone);
    const invalid = badFormat || check.status === 'invalid' || check.status === 'unverified';
    let helper = helperText;
    if (badFormat) helper = 'Solo números, con código de país si no es de Chile: +56912345678';
    else if (check.status === 'invalid') helper = `No es un número válido: ${check.error}.`;
    else if (check.status === 'unverified') helper = 'No se pudo verificar: Twilio no responde. Sal del campo para reintentar en unos minutos.';
    else if (check.status === 'checking') helper = 'Verificando el número…';
    else if (check.status === 'valid') helper = `Número válido${check.national ? `: ${check.national}` : ''}.`;

    const endAdornment = check.status === 'checking'
        ? <CircularProgress size={18} />
        : check.status === 'valid' && <CheckCircleOutlineIcon color="success" fontSize="small" />;

    return (
        <TextField
            {...props}
            value={phone}
            error={invalid}
            helperText={helper}
            onChange={(event) => onChange(normalizePhoneInput(event.target.value))}
            onBlur={validate}
            InputProps={{
                ...InputProps,
                endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : InputProps?.endAdornment,
            }}
        />
    );
};

export default PhoneField;
